import {
  CompactTimeMaterialResponse,
  EstimateLineItem,
  TimeMaterialResponse,
  TimeMaterialStatus,
  TimeMaterialId,
  ProductStatus,
  EstimateGroupId,
  TMFeeId,
} from './../../entities/tm/TimeMaterialResponse';
import { TMCategoryAdjustmentRequest, TMFeeAdjustmentRequest } from '@business/entities/tm/TMAdjustmentResponse';
import {
  OrderId,
  OrderProductId,
  OrderRequest,
  OrderShipmentId,
  OrderShipmentRequest,
  OrderShipmentStatus,
  OrderVendorId,
} from '@business/entities/order';
import { OrgId, TimeTrackingInfoResponse, JustAddressReponse, PaymentId } from '@business/entities/organization';
import { InvoiceId } from '@business/entities/TransactionInvoice';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { Repository } from '@core/utils/repository';
import { showConfirmation, showDeleteConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  OrderCancelRequest,
  OrderEmailTemplateRequest,
  OrderEstimateId,
  OrderShippingRequest,
  ReceiveItemRequest,
} from './../../entities/order/OrderResponse';
import { OrderStageId } from './../../entities/order/OrderStageResponse';
import { PurchaseOrderId } from './../../entities/order/PurchaseOrderResponse';
import { TimeMaterialAPI } from '@business/api/timeMaterial';
import { sendEmailTimeTrackingRequest, TimeLogId } from '@business/entities/time_log';
import { UserId } from '@business/entities/account';
import { POStatusID } from '@business/entities/poStatus';
import { ProcessTmInvoiceFormData } from '@modules/organizations/OrganizationPage/Components/Billing/TimeMaterialPaymentModal/TimeMaterialPaymentModal';
import { GeneratePoProductFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/GeneratePoModal/GeneratePoModal';
import { R } from '@core/utils/r';
import { TimeMaterialStepResponse } from '@business/entities/tm/TimeMaterialSectionResponse';
import { TaskPriority } from '@business/constants/task';
import { ClickupTaskRequest } from '@business/entities/clickup';
import { ClickupAPI } from '@business/api/clickup';
import { TimeMaterialTemplateId } from '@business/entities/tm';
import { TMTransactionInvoiceFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/EditTransactionInvoiceOverlay/EditTransactionInvoiceModal';
import { ServiceProviderId } from '@business/entities/tm/TmServiceProviderResponse';

export class TimeMaterialBloc extends BaseBloc {
  private readonly itemRepo = new Repository<CompactTimeMaterialResponse | TimeMaterialResponse>({
    getItemId: item => item.id,
  });

  readonly orders$ = this.itemRepo.items$;
  readonly selectOrder = this.itemRepo.selectItem;

  readonly selectStep = (timeMaterialId: TimeMaterialId, stepId: string) =>
    this.itemRepo.selectItem(timeMaterialId).pipe(
      map(timeMaterial => {
        if (!timeMaterial) return undefined;
        const allSteps = R.flatMap((timeMaterial as TimeMaterialResponse).tmSections || [], tmSections => tmSections.steps);
        return allSteps.find(s => s.id === stepId);
      }),
    );
  onReset() {
    this.itemRepo.reset();
  }

  fetchOrders = (orgId?: OrgId) => {
    return TimeMaterialAPI.fetchOrders(orgId).pipe(this.itemRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  fetchOrgOrders = (orgId: OrgId) => {
    return TimeMaterialAPI.fetchOrgOrders(orgId).pipe(this.itemRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  fetchVeryCompactOrders = (orgId: OrgId) => {
    return TimeMaterialAPI.fetchVeryCompactOrders(orgId).pipe(handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  searchOrders = (search: string) => {
    return TimeMaterialAPI.searchOrders(search).pipe(handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  fetchOrder = (id: OrderProductId, fromNgrok = false, isInvoice?: boolean, isPublic = false, withLogo = false) => {
    return TimeMaterialAPI.fetchOrder(id, isInvoice, isPublic, withLogo).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_error') }),
    );
  };

  createOrder = (data: OrderRequest) => {
    return TimeMaterialAPI.createOrder(data).pipe(
      this.itemRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order'),
      }),
    );
  };

  updateEstimate = (id: OrderId, data: OrderRequest) => {
    return TimeMaterialAPI.updateEstimate(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateInvoice = (id: InvoiceId, data: OrderRequest) => {
    return TimeMaterialAPI.updateInvoice(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateInvoicePercentage = (id: TimeMaterialId, data: TMTransactionInvoiceFormData) => {
    return TimeMaterialAPI.updateInvoicePercentage(id, data).pipe(
      tap(newInvoice => {
        const item = this.itemRepo.getItem(id)! as TimeMaterialResponse;

        const newInvoices = data.invoiceId
          ? item.invoices.map(invoice => {
              if (invoice.id === data.invoiceId) {
                return { ...invoice, ...newInvoice };
              }
              return invoice;
            })
          : [...item.invoices, newInvoice];

        this.itemRepo.updateOne({
          id,
          changes: () => ({ invoices: newInvoices }),
        });
      }),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateInvoiceDueDate = (id: InvoiceId, tmId: TimeMaterialId, dueDate: number) => {
    return TimeMaterialAPI.updateInvoiceDueDate(id, dueDate).pipe(
      tap(newInvoice => {
        const item = this.itemRepo.getItem(tmId)! as TimeMaterialResponse;

        const newInvoices = item.invoices.map(invoice => {
          if (invoice.id === id) {
            return { ...invoice, ...newInvoice };
          }
          return invoice;
        });

        this.itemRepo.updateOne({
          id: tmId,
          changes: () => ({ invoices: newInvoices }),
        });
      }),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  createInvoice = (id: OrderId, data: OrderRequest) => {
    return TimeMaterialAPI.createInvoice(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateOrder = (id: OrderId, data: Partial<OrderRequest>, isSend = false) => {
    return TimeMaterialAPI.updateOrder(id, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  deleteOrder = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete order', `Do you really want to remove this order?`).pipe(
      switchMap(() => TimeMaterialAPI.deleteOrder(id)),
      this.itemRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order'),
      }),
    );
  };

  updateStage = (
    id: OrderId,
    stageId: OrderStageId,
    deliveryDate?: number,
    closeIt = false,
    sendIt?: boolean,
    emailData?: OrderEmailTemplateRequest,
  ) => {
    return TimeMaterialAPI.updateStage(id, stageId, deliveryDate, closeIt, sendIt, emailData).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_stage'),
      }),
    );
  };

  updateStatus = (id: OrderId, status: TimeMaterialStatus, reason?: string, isSend?: boolean, data?: OrderCancelRequest) => {
    return TimeMaterialAPI.updateStatus(id, status, reason, isSend, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_status'),
      }),
    );
  };

  updateShipping = (id: OrderId, stageId: OrderStageId, data: OrderShippingRequest) => {
    return TimeMaterialAPI.updateShipping(id, stageId, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_stage'),
      }),
    );
  };

  acceptEstimate = (id: OrderEstimateId, name: string, email: string) => {
    return showConfirmation(`Accept ${gcLabel('Estimate')}`, `Do you really want to accept this ${gcLabel('estimate')}?`).pipe(
      switchMap(() => TimeMaterialAPI.acceptEstimate(id, name, email)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('accept_order'),
      }),
    );
  };

  createOrderInvoice = (id: OrderEstimateId, invoiceId: InvoiceId, sent: boolean, data?: Partial<OrderRequest>) => {
    return TimeMaterialAPI.createOrderInvoice(id, invoiceId, sent, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_status'),
      }),
    );
  };

  sendEstimate = (id: OrderEstimateId, data?: Partial<OrderRequest>, withoutAsking = false) => {
    if (withoutAsking) {
      return TimeMaterialAPI.sendEstimate(id, data).pipe(
        this.itemRepo.ops.upsertOne(item => ({ item })),
        handleMessage({
          type: requestMessage('accept_order'),
        }),
      );
    }
    return showConfirmation(`Send ${gcLabel('Estimate')}`, `Do you really want to send this ${gcLabel('estimate')} to customer?`).pipe(
      switchMap(() => TimeMaterialAPI.sendEstimate(id, data)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('accept_order'),
      }),
    );
  };

  rejectEstimate = (id: OrderEstimateId, reason: string) => {
    return TimeMaterialAPI.rejectEstimate(id, reason).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('reject_order'),
      }),
    );
  };

  sendPurchaseOrder = (id: OrderEstimateId, vendorId: OrderVendorId, data: Partial<OrderEmailTemplateRequest>, poId: string) => {
    return TimeMaterialAPI.sendPurchaseOrder(id, vendorId, data, poId).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('send_purchase_order'),
      }),
    );
  };

  createPurchaseOrder = (id: OrderId, vendorId?: OrderVendorId, address?: JustAddressReponse) => {
    return TimeMaterialAPI.createPurchaseOrder(id, vendorId, address).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  closePurchaseOrder = (poId: PurchaseOrderId) => {
    return showDeleteConfirmation('Cancel Purchase Order', `Do you really want to cancel this purchase order?`, {
      btnLabel: 'Confirm',
    }).pipe(
      switchMap(() => TimeMaterialAPI.closePurchaseOrder(poId)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_product'),
      }),
    );
  };

  purchaseOrderStatus = (poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>) => {
    return TimeMaterialAPI.purchaseOrderStatus(poId, status, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  updatePOStatus = (poId: PurchaseOrderId, status: POStatusID) => {
    return TimeMaterialAPI.updatePoStatus(poId, status).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  updatePurchaseOrder = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return TimeMaterialAPI.updatePurchaseOrder(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };
  saveBillOfLading = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return TimeMaterialAPI.saveBillOfLading(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  savePickList = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return TimeMaterialAPI.savePickList(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  download = (orderId: OrderId, vendorId: OrderVendorId, filename: string) => {
    return TimeMaterialAPI.download(orderId, vendorId, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  downloadLading = (id: PurchaseOrderId, filename: string) => {
    return TimeMaterialAPI.downloadLading(id, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  downloadPickList = (id: PurchaseOrderId, filename: string) => {
    return TimeMaterialAPI.downloadPickList(id, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  createShipment = (poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>, isSend?: boolean) => {
    return TimeMaterialAPI.createShipment(poId, status, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  createShipmentWithOutPO = (id: TimeMaterialId, data: Partial<OrderShipmentRequest>, isSend?: boolean) => {
    return TimeMaterialAPI.createShipmentWithOutPO(id, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  updateShipment = (id: OrderShipmentId, data: OrderShippingRequest, isSend?: boolean) => {
    return TimeMaterialAPI.updateShipment(id, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_shipment'),
      }),
    );
  };

  deleteShipment = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete Shipment', `Do you really want to remove this shipment?`).pipe(
      switchMap(() => TimeMaterialAPI.deleteShipment(id)),
      this.itemRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_shipment'),
      }),
    );
  };

  deleteInvoice = (id: InvoiceId, orderId: OrderId) => {
    return showDeleteConfirmation('Delete Invoice', `Do you really want to remove this invoice?`).pipe(
      switchMap(() => TimeMaterialAPI.deleteInvoice(id, orderId)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_invoice'),
      }),
    );
  };

  updateEstimateLineItem = (orderId: TimeMaterialId, id: number, data: Partial<EstimateLineItem>) => {
    return TimeMaterialAPI.updateEstimateLineItem(id, data).pipe(
      this.itemRepo.ops.updateOne(estimate => ({
        id: orderId,
        changes: () => ({ estimate }),
      })),
    );
  };

  updateItemsStatus = (orderId: string, status: ProductStatus, itemIds: number[], date?: number) => {
    return TimeMaterialAPI.updateItemsStatus(orderId, status, itemIds, date).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  receiveItems = (id: OrderId, data: ReceiveItemRequest[]) => {
    return TimeMaterialAPI.receiveItems(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('tm_receive_items'),
      }),
    );
  };
  getTimeTrackingReport = (id: OrderProductId) => {
    return TimeMaterialAPI.getTimeTrackingReport(id).pipe(handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  sendEmail = (orderId: string, data: sendEmailTimeTrackingRequest) => {
    return TimeMaterialAPI.sendEmail(orderId, data).pipe(
      handleMessage({
        error: requestMessage('time_tracker_start_error'),
      }),
    );
  };

  selectManager = (orderId: TimeMaterialId, userId: UserId) => {
    return TimeMaterialAPI.selectManager(orderId, userId).pipe(
      this.itemRepo.ops.updateOne(projectManager => ({
        id: orderId,
        changes: () => ({ projectManager }),
      })),
    );
  };

  updateDates = (orderId: TimeMaterialId, data: Partial<CompactTimeMaterialResponse>) => {
    return TimeMaterialAPI.updateDates(orderId, data).pipe(
      this.itemRepo.ops.updateOne(dueDate => ({
        id: orderId,
        changes: () => ({ ...dueDate }),
      })),
    );
  };

  updateUsers = (id: TimeMaterialId, users: string[]) => {
    return TimeMaterialAPI.updateUsers(id, users).pipe(
      this.itemRepo.ops.updateOne(users => ({
        id,
        changes: () => ({ users }),
      })),
    );
  };

  processTmInvoicePayment = (id: TimeMaterialId, paymentId: PaymentId, amount: number, data: ProcessTmInvoiceFormData) => {
    return TimeMaterialAPI.processTmInvoicePayment(id, paymentId, amount, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_status'),
      }),
    );
  };

  //   stopTimeLog = (id: TimeLogId, data: TimeTrackingInfoResponse) => {
  //     return TimeLogAPI.stopTimeLog(id, data).pipe(
  //       this.itemRepo.ops.upsertOne(item => ({ item })),
  //       handleMessage({
  //         type: requestMessage('stop_time_log'),
  //       }),
  //     );
  //   };

  createBulkPurchaseOrder = (id: OrderId, address: JustAddressReponse, data: GeneratePoProductFormData) => {
    return TimeMaterialAPI.createBulkPurchaseOrder(id, address, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  // Summary tab adjustments: saved with the required note; the backend keeps each change in the line's history
  updateAdjustment = (id: EstimateGroupId, data: TMCategoryAdjustmentRequest) => {
    return TimeMaterialAPI.updateAdjustment(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('update_tm_adjustment') }),
    );
  };

  updateFeeAdjustment = (id: TMFeeId, data: TMFeeAdjustmentRequest) => {
    return TimeMaterialAPI.updateFeeAdjustment(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('update_tm_adjustment') }),
    );
  };

  fetchAdjustments = (id: EstimateGroupId) => {
    return TimeMaterialAPI.fetchAdjustments(id).pipe(handleMessage({ error: requestMessage('fetch_tm_adjustments_error') }));
  };

  fetchFeeAdjustments = (id: TMFeeId) => {
    return TimeMaterialAPI.fetchFeeAdjustments(id).pipe(handleMessage({ error: requestMessage('fetch_tm_adjustments_error') }));
  };

  orderTodos = (timeMaterialId: TimeMaterialId, items: any[]) => {
    return TimeMaterialAPI.orderTodos(timeMaterialId, items).pipe(this.itemRepo.ops.upsertOne(item => ({ item })));
  };

  /* Sections */
  createSection = (timeMaterialId: TimeMaterialId, name: string, isPublic = false) => {
    return TimeMaterialAPI.createSection(timeMaterialId, name, undefined, isPublic).pipe(
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),
      handleMessage({
        type: requestMessage('create_template_section'),
      }),
    );
  };

  updateSection = (timeMaterialId: TimeMaterialId, sectionId: string, name: string, isPublic = false, orgProjId?: TimeMaterialId) => {
    const oldTimeMaterialId = orgProjId || timeMaterialId;
    return TimeMaterialAPI.updateSection(sectionId, name, undefined, isPublic, timeMaterialId).pipe(
      this.itemRepo.ops.updateOne(tmSections => {
        return {
          id: oldTimeMaterialId,
          changes: project => {
            return { tmSections: tmSections };
          },
        };
      }),
      handleMessage({
        type: requestMessage('update_template_section'),
      }),
    );
  };

  deleteSection = (timeMaterialId: TimeMaterialId, id: string) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => TimeMaterialAPI.deleteSection(id)),
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),

      handleMessage({
        type: requestMessage('delete_template_section'),
      }),
    );
  };

  duplicateSection = (timeMaterialId: TimeMaterialId, id: string) => {
    return showConfirmation('Duplicate Section', 'Do you really want to duplicate this Section?').pipe(
      switchMap(() => TimeMaterialAPI.duplicateSection(id)),
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),

      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  duplicateStep = (timeMaterialId: TimeMaterialId, id: string) => {
    return TimeMaterialAPI.duplicateStep(id).pipe(
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),

      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  sortSections = (timeMaterialId: TimeMaterialId, sectionIds: string[]) => {
    return TimeMaterialAPI.sortSections(sectionIds).pipe(
      tap(() => {
        const timeMaterial = this.itemRepo.getItem(timeMaterialId)! as TimeMaterialResponse;
        const newSections = timeMaterial.tmSections.map(sec => {
          return { ...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id) };
        });

        this.itemRepo.updateOne({
          id: timeMaterialId,
          changes: () => ({ tmSections: R.sortBy(newSections, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_template_section_sort'),
      }),
    );
  };

  assignSection = (timeMaterialId: TimeMaterialId, sectionId: string, users: UserId[]) => {
    return TimeMaterialAPI.assignSection(timeMaterialId, sectionId, users).pipe(
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),
    );
  };

  assignSectionUsers = (timeMaterialId: TimeMaterialId, sectionId: string, users: UserId[]) => {
    return TimeMaterialAPI.assignSectionUsers(timeMaterialId, sectionId, users).pipe(
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),
    );
  };

  fetchStep = (timeMaterialId: TimeMaterialId, id: string) => {
    return TimeMaterialAPI.fetchStep(id).pipe(
      //   this.itemRepo.ops.updateOne(tmSections => ({
      //     id: timeMaterialId,
      //     changes: () => ({ tmSections }),
      //   })),
      handleMessage({
        error: requestMessage('fetch_step'),
      }),
    );
  };

  loadStep = (timeMaterialId: TimeMaterialId, stepId: number) => {
    return TimeMaterialAPI.loadStep(stepId).pipe(
      this.itemRepo.ops.updateOne(timeMaterialSection => ({
        id: timeMaterialId,
        changes: timeMaterial => {
          const p = timeMaterial as TimeMaterialResponse;
          const tmSections = p.tmSections.map(s => {
            if (s.id == timeMaterialSection.id) {
              return tmSections;
            } else {
              return s;
            }
          });
          return { tmSections };
        },
      })),
    );
  };

  createStep = (timeMaterialId: TimeMaterialId, sectionId: string, data: Partial<TimeMaterialStepResponse>, parentStepId?: string) => {
    return TimeMaterialAPI.createStep(sectionId, data, parentStepId, undefined, timeMaterialId).pipe(
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),

      handleMessage({
        error: requestMessage('create_template_section_step_error'),
      }),
    );
  };

  updateStep = (timeMaterialId: TimeMaterialId, id: string, data: Partial<TimeMaterialStepResponse>, success = false) => {
    return TimeMaterialAPI.updateStep(id, data).pipe(
      this.itemRepo.ops.updateOne(section => ({
        id: timeMaterialId,
        changes: timeMaterial => {
          const p = timeMaterial as TimeMaterialResponse;
          const tmSections = p.tmSections.map(s => {
            if (s.id == section.id) {
              return section;
            }
            return s;
          });
          return { tmSections };
        },
      })),

      success
        ? handleMessage({
            type: requestMessage('update_template_section_step'),
          })
        : handleMessage({
            error: requestMessage('save_step'),
          }),
    );
  };

  toggleStep = (timeMaterialId: TimeMaterialId, stepId: number, completeAll?: boolean) => {
    return TimeMaterialAPI.toggleStep(stepId, completeAll).pipe(
      this.itemRepo.ops.updateOne(section => ({
        id: timeMaterialId,
        changes: timeMaterial => {
          const p = timeMaterial as TimeMaterialResponse;
          const tmSections = p.tmSections.map(s => {
            if (s.id == section.id) {
              return section;
            } else {
              return s;
            }
          });
          return { tmSections };
        },
      })),
    );
  };

  sortSectionSteps = (timeMaterialId: TimeMaterialId, sectionId: string, stepIds: string[], parentStepId?: string) => {
    return TimeMaterialAPI.sortSectionSteps(stepIds).pipe(
      tap(() => {
        const timeMaterial = this.itemRepo.getItem(timeMaterialId)! as TimeMaterialResponse;

        const affectedSection = R.find(timeMaterial.tmSections, s => s.id === sectionId)!;
        const newSteps = affectedSection.steps.map(step => {
          if (parentStepId) {
            const newSubSteps = step.subSteps.map(subStep => ({ ...subStep, menuOrder: R.findIndex(stepIds, i => i === subStep.id) }));
            return { ...step, subSteps: R.sortBy(newSubSteps, s => s.menuOrder) };
          }
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = timeMaterial.tmSections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        this.itemRepo.updateOne({
          id: timeMaterialId,
          changes: () => ({ tmSections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  deleteStep = (timeMaterialId: TimeMaterialId, id: string) => {
    return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
      switchMap(() => TimeMaterialAPI.deleteStep(id)),
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),

      handleMessage({
        type: requestMessage('delete_template_section_step'),
      }),
    );
  };

  setTimeMaterialStepPriority = (priority: TaskPriority, stepId: string, timeMaterialId: TimeMaterialId) => {
    return TimeMaterialAPI.setTimeMaterialStepPriority(stepId, priority).pipe(
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),
      handleMessage({
        error: requestMessage('project_step_priority'),
      }),
    );
  };

  unSyncClickupTask = (request: ClickupTaskRequest) => {
    return showDeleteConfirmation('Unsync', 'Do you really want to unsync this task from clickup?').pipe(
      switchMap(() => ClickupAPI.unSync(request)),
      handleMessage({
        type: requestMessage('unsync_task_from_clickup'),
      }),
    );
  };

  assignTimeMaterialStep = (timeMaterialId: TimeMaterialId, stepId: string, data: string[]) => {
    return TimeMaterialAPI.assignTimeMaterialStep(stepId, data).pipe(
      this.itemRepo.ops.updateOne(tmSections => ({
        id: timeMaterialId,
        changes: () => ({ tmSections }),
      })),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  createClickupTask = (request: ClickupTaskRequest, projectId: string, stepId: string) => {
    return ClickupAPI.createTask(request).pipe(
      handleMessage({
        type: requestMessage('sync_task_to_clickup'),
      }),
    );
  };

  selectTemplate = (timeMaterialId: TimeMaterialId, templateId: TimeMaterialTemplateId) => {
    return TimeMaterialAPI.selectTemplate(timeMaterialId, templateId).pipe(this.itemRepo.ops.upsertOne(item => ({ item })));
  };

  changeTemplate = (timeMaterialId: TimeMaterialId, templateId: TimeMaterialTemplateId) => {
    return TimeMaterialAPI.changeTemplate(timeMaterialId, templateId).pipe(this.itemRepo.ops.upsertOne(item => ({ item })));
  };

  updateProvider = (id: EstimateGroupId, providerId: ServiceProviderId) => {
    return TimeMaterialAPI.updateProvider(id, providerId).pipe(this.itemRepo.ops.upsertOne(item => ({ item })));
  };
}
